import { $createAtMentionNode } from '@adamdotai/editor/AtMentionsPlugin'
import { $createHashtagMentionNode } from '@adamdotai/editor/HashtagMentionsPlugin'
import { $createAdamParagraphNode } from '@adamdotai/editor/NotesPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { EditorState } from 'lexical'
import { $createTextNode, $getRoot, TextNode } from 'lexical'
import { useEffect } from 'react'

export const isHashtagRegex =
    /#<([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})><(Meeting|Group|Note|Agenda|Attachment|Poll|Action|Decision|Tag|team|project|committee|board|MOM)>(?:<([0-9a-f\u0600-\u06FF]{8}-(?:[0-9a-f\u0600-\u06FF]{4}-){3}[0-9a-f\u0600-\u06FF]{12})>|<Empty Meeting>)([\s\S]*?)\s*\*/g

export const EditorInputPlugin = ({
    state,
    isEditingMode,
    setInitialState,
}: {
    state: string
    isEditingMode: boolean
    setInitialState: (initialState: EditorState) => void
}) => {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        if (isEditingMode) {
            setInitialState(editor.getEditorState())
        }
    }, [editor, isEditingMode, setInitialState])

    useEffect(() => {
        editor.update(() => {
            const node = $createAdamParagraphNode()
            const text = $createTextNode(state)
            const root = $getRoot()
            root.clear()
            root.append(node)
            node.append(text)
        })

        editor.registerNodeTransform(TextNode, (node) => {
            const text = node.getTextContent()

            const isMeetingRegex = /@Meeting\*/g
            const isMentionRegex =
                /@<([0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12})>([\w\u0600-\u06FF\s$&+,:;=?@#|'<>.^()%!-/\\]*?)\s*\*/g

            const meetingMatch = isMeetingRegex.exec(text)
            const mentionMatch = isMentionRegex.exec(text)
            const HashtagMatch = isHashtagRegex.exec(text)

            if (meetingMatch) {
                const [matchText] = meetingMatch

                let targetNode
                if (meetingMatch.index === 0) {
                    ;[targetNode] = node.splitText(
                        meetingMatch.index,
                        meetingMatch.index + matchText.length,
                    )
                } else {
                    ;[, targetNode] = node.splitText(
                        meetingMatch.index,
                        meetingMatch.index + matchText.length,
                    )
                }

                const mentionNode = $createAtMentionNode({
                    type: 'meeting',
                })

                targetNode.replace(mentionNode)
            }

            if (mentionMatch) {
                const [matchText] = mentionMatch

                let targetNode
                if (mentionMatch.index === 0) {
                    ;[targetNode] = node.splitText(
                        mentionMatch.index,
                        mentionMatch.index + matchText.length,
                    )
                } else {
                    ;[, targetNode] = node.splitText(
                        mentionMatch.index,
                        mentionMatch.index + matchText.length,
                    )
                }

                const [, userId, attendeeName] = mentionMatch

                const mentionNode = $createAtMentionNode({
                    type: 'person',
                    userId,
                    attendeeName,
                    user: {
                        id: userId,
                        name: attendeeName,
                        email: '',
                        imageUrl: '',
                        meetplaceId: '',
                        role: '',
                    },
                })

                targetNode.replace(mentionNode)
            } else if (HashtagMatch) {
                const [matchText] = HashtagMatch

                let targetNode
                if (HashtagMatch.index === 0) {
                    ;[targetNode] = node.splitText(
                        HashtagMatch.index,
                        HashtagMatch.index + matchText.length,
                    )
                } else {
                    ;[, targetNode] = node.splitText(
                        HashtagMatch.index,
                        HashtagMatch.index + matchText.length,
                    )
                }

                const itemId = HashtagMatch[1]
                const itemType = HashtagMatch[2]
                const meetingId = HashtagMatch[3]
                const itemTitle = HashtagMatch[4]

                const hashtagNode = $createHashtagMentionNode({
                    id: itemId,
                    title: itemTitle,
                    type: itemType as any,
                    meeting: {
                        id: meetingId,
                        isAllDay: false,
                        meetingEndDateUtc: new Date() as any,
                        meetingStartDateUtc: new Date() as any,
                    },
                })

                targetNode.replace(hashtagNode)
            }
        })
    }, [editor, state])

    return null
}
